import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
	SidebarGroupLabel,
	SidebarGroupContent,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useUser } from "@/hooks/useUser";
import {
	MoreHorizontal,
	Settings,
	User as UserIcon,
	Play,
	Upload,
	Activity,
	Tag,
	FileText,
	Calendar,
	Layers,
	Hand,
	LogOut,
} from "lucide-react";
import { ConbersaLogo } from "./ConbersaLogo";
import { useNavigate, useLocation } from "react-router";
import { useLogout } from "@/hooks/useLogout";

type NavItem = {
	label: string;
	to: string;
	icon: React.ComponentType<{ size?: number; className?: string }>;
};

const OPERATE_ITEMS: NavItem[] = [
	{ label: "Runs", to: "/runs", icon: Play },
	{ label: "Manual", to: "/manual", icon: Hand },
	{ label: "Sessions", to: "/sessions", icon: Layers },
	{ label: "Content", to: "/content-upload", icon: Upload },
	{ label: "Schedule", to: "/schedule", icon: Calendar },
	{ label: "Activity", to: "/agent-activity", icon: Activity },
];

const LIBRARY_ITEMS: NavItem[] = [
	{ label: "Brands", to: "/brands", icon: Tag },
	{ label: "Prompts", to: "/prompts", icon: FileText },
];

export function AppSidebar() {
	const { data: user, isLoading: isUserLoading } = useUser();
	const navigate = useNavigate();
	const location = useLocation();
	const logout = useLogout();

	const isActive = (to: string) =>
		location.pathname === to || location.pathname.startsWith(`${to}/`);

	const renderUserAvatar = () => {
		if (isUserLoading) {
			return <div className="w-6 h-6 rounded-full bg-gray-300 animate-pulse mr-2" />;
		}

		return (
			<Avatar className="w-6 h-6 mr-2">
				<AvatarFallback className="bg-gradient-to-tr from-primary to-secondary text-primary-foreground">
					<UserIcon size={16} />
				</AvatarFallback>
			</Avatar>
		);
	};

	const renderItem = (item: NavItem) => {
		const Icon = item.icon;
		const active = isActive(item.to);
		return (
			<SidebarMenuItem key={item.to}>
				<SidebarMenuButton
					className={`w-full justify-start text-sm rounded-lg cursor-pointer ${
						active
							? "bg-muted text-foreground font-semibold"
							: "text-foreground/70 hover:bg-muted/50 hover:text-foreground"
					}`}
					onClick={() => navigate(item.to)}
				>
					<Icon size={16} className="mr-2" />
					{item.label}
				</SidebarMenuButton>
			</SidebarMenuItem>
		);
	};

	return (
		<>
			<Sidebar className="bg-background border-r flex flex-col h-full data-[state=closed]:border-r-0">
				<SidebarHeader className="h-16 px-4 border-b flex flex-row items-center">
					<div className="flex w-full items-center justify-between">
						<div className="flex items-center space-x-2">
							<ConbersaLogo size={24} className="drop-shadow-sm" />
							<span className="font-semibold text-md text-foreground">Conbersa</span>
						</div>
					</div>
				</SidebarHeader>

				<SidebarContent className="flex-grow p-0">
					<ScrollArea className="h-full py-4 px-2 w-full">
						<SidebarGroup className="mb-4">
							<SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1 px-2">
								Operate
							</SidebarGroupLabel>
							<SidebarGroupContent>
								<SidebarMenu className="space-y-1">
									{OPERATE_ITEMS.map(renderItem)}
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>

						<SidebarGroup>
							<SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1 px-2">
								Library
							</SidebarGroupLabel>
							<SidebarGroupContent>
								<SidebarMenu className="space-y-1">
									{LIBRARY_ITEMS.map(renderItem)}
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>
					</ScrollArea>
				</SidebarContent>

				<SidebarFooter className="p-4 border-t space-y-2">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" className="w-full justify-start text-sm text-foreground/70 hover:bg-muted/50 hover:text-foreground">
								{renderUserAvatar()}
								<span className="truncate">
									{isUserLoading ? 'Loading...' : user?.fullName}
								</span>
								<MoreHorizontal size={16} className="ml-auto opacity-50 group-hover:opacity-100" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-56" align="end" forceMount>
							<DropdownMenuLabel className="font-normal">
								<div className="flex flex-col space-y-1">
									<p className="text-sm font-medium leading-none">{user?.fullName}</p>
									<p className="text-xs leading-none text-muted-foreground">
										{user?.email}
									</p>
								</div>
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => navigate('/settings')}>
								<Settings className="mr-2 h-4 w-4" />
								<span>Settings</span>
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => logout.mutate()}>
								<LogOut className="mr-2 h-4 w-4" />
								<span>Log out</span>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarFooter>
			</Sidebar>
		</>
	);
}
